import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  auditTime,
  catchError,
  filter,
  fromEvent,
  map,
  merge,
  of,
  startWith,
  switchMap,
  tap
} from 'rxjs';

import { PanelActivityStatusResponse } from '../models/panel-administracion';
import { AdminBudgetsService } from './admin-budgets.service';
import { AdminPanelService } from './admin-panel.service';
import { AdminRequestsService } from './admin-requests.service';
import { ChatSocketService } from './chat-socket.service';

export interface AdminCountersState {
  pendingRequests: number;
  requestIncidents: number;
  openRequests: number;
  pendingBudgets: number;
  budgetIncidents: number;
  openBudgets: number;
  loading: boolean;
  stale: boolean;
  generatedAt: string | null;
  lastUpdatedAt: string | null;
  error: string | null;
}

interface AdminCountersRefreshResult {
  response: PanelActivityStatusResponse | null;
  error: unknown | null;
}

const EMPTY_ADMIN_COUNTERS_STATE: AdminCountersState = {
  pendingRequests: 0,
  requestIncidents: 0,
  openRequests: 0,
  pendingBudgets: 0,
  budgetIncidents: 0,
  openBudgets: 0,
  loading: false,
  stale: false,
  generatedAt: null,
  lastUpdatedAt: null,
  error: null
};

@Injectable({
  providedIn: 'root'
})
export class AdminCountersService implements OnDestroy {
  private readonly stateSubject = new BehaviorSubject<AdminCountersState>({ ...EMPTY_ADMIN_COUNTERS_STATE });
  private readonly refreshSubject = new Subject<string>();
  private subscriptions = new Subscription();
  private active = false;

  readonly state$ = this.stateSubject.asObservable();

  constructor(
    private adminPanelService: AdminPanelService,
    private requestsService: AdminRequestsService,
    private budgetsService: AdminBudgetsService,
    private router: Router,
    private chatSocket: ChatSocketService
  ) {}

  ngOnDestroy(): void {
    this.stop();
  }

  public start(): void {
    if (this.active) {
      return;
    }

    this.active = true;

    const triggers: Observable<unknown>[] = [
      this.refreshSubject,
      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map(() => 'navigation')
      ),
      this.requestsService.pendientesRefresh$.pipe(map(() => 'requests-local-refresh')),
      this.budgetsService.pendientesRefresh$.pipe(map(() => 'budgets-local-refresh')),
      this.chatSocket.connectionState$.pipe(
        filter((state) => state === 'open'),
        map(() => 'socket-open')
      ),
      this.chatSocket.events$.pipe(
        filter((event) => event.type === 'admin_status.invalidate'),
        map(() => 'socket-admin-status-invalidate')
      )
    ];

    if (typeof window !== 'undefined') {
      triggers.push(fromEvent(window, 'focus').pipe(map(() => 'window-focus')));
    }

    if (typeof document !== 'undefined') {
      triggers.push(
        fromEvent(document, 'visibilitychange').pipe(
          filter(() => document.visibilityState === 'visible'),
          map(() => 'document-visible')
        )
      );
    }

    this.subscriptions.add(
      merge(...triggers)
        .pipe(
          startWith('start'),
          auditTime(150),
          tap(() => this.markLoading()),
          switchMap(() => this.loadCounters())
        )
        .subscribe((result) => this.applyRefreshResult(result))
    );

    this.chatSocket.connect();
  }

  public stop(): void {
    if (!this.active) {
      return;
    }

    this.active = false;
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.stateSubject.next({ ...EMPTY_ADMIN_COUNTERS_STATE });
  }

  public refresh(reason = 'manual'): void {
    if (!this.active) {
      return;
    }

    this.refreshSubject.next(reason);
  }

  private loadCounters(): Observable<AdminCountersRefreshResult> {
    return this.adminPanelService.getActivityStatus().pipe(
      map((response) => ({ response, error: null })),
      catchError((error) => of({ response: null, error }))
    );
  }

  private markLoading(): void {
    const current = this.stateSubject.value;
    this.stateSubject.next({
      ...current,
      loading: true,
      error: null
    });
  }

  private applyRefreshResult(result: AdminCountersRefreshResult): void {
    if (!this.active) {
      return;
    }

    if (result.response) {
      const pendingRequests = result.response.pending_requests ?? 0;
      const requestIncidents = result.response.request_incidents ?? 0;
      const pendingBudgets = result.response.pending_budgets ?? 0;
      const budgetIncidents = result.response.budget_incidents ?? 0;

      this.stateSubject.next({
        pendingRequests,
        requestIncidents,
        openRequests: pendingRequests + requestIncidents,
        pendingBudgets,
        budgetIncidents,
        openBudgets: pendingBudgets + budgetIncidents,
        loading: false,
        stale: false,
        generatedAt: result.response.generated_at ?? null,
        lastUpdatedAt: new Date().toISOString(),
        error: null
      });
      return;
    }

    const current = this.stateSubject.value;
    this.stateSubject.next({
      ...current,
      loading: false,
      stale: true,
      error: 'No se pudieron actualizar los contadores.'
    });
  }
}
